import {
    CurrencyCode,
    getCurrencyInfo,
    DEFAULT_CURRENCY,
} from "@/models/currency";

/**
 * Formatta un prezzo con il simbolo della valuta corretta
 * Usa Intl.NumberFormat per formattazione locale corretta
 * Arrotonda i prezzi agli interi (senza decimali)
 *
 * @param amount - Importo da formattare (string o number)
 * @param currency - Codice valuta (EUR, USD, GBP, CAD, AUD)
 * @returns Prezzo formattato con simbolo valuta arrotondato (es: "$543" o "€500")
 *
 * @example
 * formatPrice(542.80, 'USD') // "$543" (arrotondato per eccesso)
 * formatPrice(542.20, 'USD') // "$542" (arrotondato per difetto)
 * formatPrice('500', 'EUR') // "€500"
 * formatPrice(430.50, 'GBP') // "£431"
 */
export const formatPrice = (
    amount: string | number,
    currency: CurrencyCode = DEFAULT_CURRENCY
): string => {
    // Converti a numero
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    // Validazione
    if (Number.isNaN(numAmount)) {
        console.warn(`Invalid amount for formatPrice: ${amount}`);
        return "0";
    }

    // Arrotonda all'intero più vicino
    const roundedAmount = Math.round(numAmount);

    try {
        // Ottieni info valuta per il locale
        const currencyInfo = getCurrencyInfo(currency);
        const locale = currencyInfo?.locale || "en-US";

        // Formatta con Intl.NumberFormat senza decimali
        const formatted = new Intl.NumberFormat(locale, {
            style: "currency",
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(roundedAmount);

        // Rimuovi spazi prima del simbolo valuta (es: "€ 500" -> "€500")
        return formatted.replace(/\s+/g, "");
    } catch (error) {
        console.error("Error formatting price:", error);
        // Fallback
        const symbol = getCurrencyInfo(currency)?.symbol || "$";
        return `${symbol}${roundedAmount}`;
    }
};

/**
 * Formatta un prezzo senza simbolo valuta, solo numero
 * Arrotonda i prezzi agli interi (senza decimali)
 *
 * @param amount - Importo da formattare
 * @param currency - Codice valuta (per determinare il formato locale)
 * @returns Prezzo formattato senza simbolo arrotondato (es: "543" o "500")
 */
export const formatPriceWithoutSymbol = (
    amount: string | number,
    currency: CurrencyCode = DEFAULT_CURRENCY
): string => {
    const numAmount = typeof amount === "string" ? parseFloat(amount) : amount;

    if (Number.isNaN(numAmount)) {
        return "0";
    }

    // Arrotonda all'intero più vicino
    const roundedAmount = Math.round(numAmount);

    try {
        const currencyInfo = getCurrencyInfo(currency);
        const locale = currencyInfo?.locale || "en-US";

        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(roundedAmount);
    } catch (error) {
        return roundedAmount.toString();
    }
};

/**
 * Ottiene solo il simbolo della valuta
 *
 * @param currency - Codice valuta
 * @returns Simbolo valuta (es: "$", "€", "£")
 */
export const getCurrencySymbol = (
    currency: CurrencyCode = DEFAULT_CURRENCY
): string => {
    return getCurrencyInfo(currency)?.symbol || "$";
};
